import { Core } from "@faire/design-tokens";
import React from "react";

import { IEmbeddedLinkTheme } from "../../ui/EmbeddedLink";
import { IButtonTheme } from "../Button";
import { Color } from "../Color";
import { Weight } from "../Font";
import { SVGIcon } from "../icons/SVGIcon";
import { TypographyTheme } from "../Typography";

import { IAvatar } from "./Avatar";
import { IBanners } from "./Banners";
import { IButtons } from "./Button";
import { ICheckboxes } from "./Checkbox";
import { IEventsThemeColors } from "./eventsThemeColors";
import { InputTheme } from "./Input";
import { IMedia2Strings } from "./Media2Strings";
import { IModalThemes } from "./Modal";
import { IPagination } from "./Pagination";
import { IPills } from "./Pill";
import { IRadios } from "./Radio";
import { StepperTheme } from "./Stepper";
import { ToggleTheme } from "./Toggle";
import { ITooltips } from "./Tooltip";

/**
 * Represents a highlight color.
 */
export interface IHighlightColor {
  readonly color: Color | "transparent";
  /**
   * Accent for the color when hovering.
   */
  readonly hover: Color | "transparent";
  /**
   * Optional explicit contrasting color, used for text above the color.
   * Defaults to richBlack or white, whichever is higher contrast.
   */
  readonly contrast: Color | "transparent";
}

/**
 * @deprecated use Typography 2 fonts instead
 */
export interface IFonts {
  readonly serif: IFontVariants;
  readonly sansSerif: IFontVariants;
}

/**
 * @deprecated use Typography 2 font variants instead
 */
export interface IFontVariant {
  readonly family: (weight: Weight) => string;
  readonly weight: Weight;
  readonly size: string;
  readonly lineHeight: string;
  readonly letterSpacing: string;
}

export interface IFontVariants {
  readonly h1: IFontVariant;
  readonly h2: IFontVariant;
  readonly h3: IFontVariant;
  readonly h4: IFontVariant;
  readonly h5: IFontVariant;
  readonly p: IFontVariant;
  readonly small: IFontVariant;
}

type CoreColors = {
  [key in keyof typeof Core]: (typeof Core)[key];
};

export interface IHighVisState {
  typography: boolean;
  layout: boolean;
  component: boolean;
}

export interface ITheme extends CoreColors {
  readonly name: string;
  readonly pills: IPills;
  readonly modals: IModalThemes;
  readonly buttons: IButtons;
  readonly buttons2: IButtonTheme;
  readonly embeddedLink: IEmbeddedLinkTheme;
  readonly tooltips: ITooltips;
  readonly checkboxes: ICheckboxes;
  readonly radios: IRadios;
  readonly toggle: ToggleTheme;
  readonly input: InputTheme;
  readonly pagination: IPagination;
  readonly stepper: StepperTheme;
  readonly avatar: IAvatar;
  readonly banners: IBanners;
  readonly fonts: IFonts;
  readonly media2strings: IMedia2Strings;
  readonly typography: TypographyTheme;
  /**
   * Spacing size in pixels. Spacing values are multiples of this number,
   * e.g. `x-small` = `1x` = 1 * [theme spacing]
   */
  readonly spacing: number;
  /**
   * Global icon used in events (ex: FSM/FFW/FFW)
   */
  readonly eventIcon?: {
    readonly Component: React.FC<SVGIcon.IProps>;
    readonly color?: Color;
    readonly backgroundColor?: Color;
  };
  /**
   * eventsThemeColors object to be used in events (ex: FSM/FFW/FFW)
   */
  readonly eventsThemeColors?: IEventsThemeColors;
  readonly highVis?: IHighVisState;
}
